/**
 * Frontend Error Reporter — POST to Worker /api/log-error
 * Captures: React component errors, window.onerror, unhandledrejection
 * Privacy: no PII in default capture, user_id only if authenticated
 */

import { supabase } from './supabase/supabaseClient';

const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'https://trans-bctn-api.dichtailieu-vdsc.workers.dev';

interface ReportPayload {
  message: string;
  stack?: string;
  action?: string;  // e.g. 'react_boundary', 'unhandled_rejection', 'window_error'
  inputSummary?: string;
}

// Dedupe: don't send same error > 3x per session
const sentFingerprints = new Map<string, number>();
const MAX_PER_FINGERPRINT = 3;
const SESSION_LIMIT = 50;  // hard cap per session
let sessionCount = 0;

function fingerprint(msg: string, stack?: string): string {
  return (msg.slice(0, 100) + '|' + (stack?.split('\n')[1] || '').slice(0, 100)).trim();
}

export async function reportError(payload: ReportPayload): Promise<void> {
  try {
    if (sessionCount >= SESSION_LIMIT) return;
    const fp = fingerprint(payload.message, payload.stack);
    const count = sentFingerprints.get(fp) || 0;
    if (count >= MAX_PER_FINGERPRINT) return;
    sentFingerprints.set(fp, count + 1);
    sessionCount++;

    // Get user_id if authenticated
    let userId: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user?.id || null;
    } catch { /* no session */ }

    const body = {
      message: payload.message,
      stack: payload.stack,
      action: payload.action || 'frontend',
      userId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      inputSummary: payload.inputSummary,
    };

    // Fire and forget — use keepalive for unload safety
    await fetch(`${WORKER_URL}/api/log-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (e) {
    // Silent fail — don't cascade errors when logging errors
    console.warn('[errorReporter] Failed to report:', e);
  }
}

/**
 * Install global handlers (call once at app boot)
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    reportError({
      message: event.message,
      stack: event.error?.stack,
      action: 'window_error',
      inputSummary: `file=${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      message: reason?.message || String(reason),
      stack: reason?.stack,
      action: 'unhandled_rejection',
    });
  });
}
